import {
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Client,
  Conversation,
  Message,
  Paginator,
} from "@twilio/conversations";
import { useLocation } from "react-router-dom";

import { ConversationContext } from "./ConversationContext";
import useVideoContext from "./useVideoContext";

interface ConversationProviderProps {
  children: ReactNode;
}

export const ConversationProvider = ({
  children,
}: ConversationProviderProps) => {
  const location = useLocation();
  const roomToken = (location.state as { roomToken?: string } | null)?.roomToken;
  const { room } = useVideoContext();
  const [isInitialized, setIsInitialized] = useState(false);
  const [client, setClient] = useState<Client | null>(null);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Paginator<Message> | null>(null);
  const [isFetchingPrevPage, setIsFetchingPrevPage] = useState(false);
  const [identity, setIdentity] = useState<string | null>(null);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const clientRef = useRef<Client | null>(null);

  useEffect(() => {
    if (room) {
      const localParticipant = room.localParticipant;
      setIdentity(localParticipant.identity);
    }
  }, [room]);

  const handleSendMessage = (message: string) => {
    if (conversation && message.trim() !== "") {
      conversation.sendMessage(message);
    }
  };

  const handleSendMediaMessage = (media: File) => {
    if (conversation && media) {
      conversation.sendMessage({
        media: media,
        contentType: media.type,
        filename: media.name,
      });
    }
  };

  const handleReadMessages = () => {
    if (conversation) {
      conversation.setAllMessagesRead();
      setUnreadMessagesCount(0);
    }
  };

  const getPrevPage = async () => {
    if (messages?.hasPrevPage && !isFetchingPrevPage) {
      setIsFetchingPrevPage(true);
      const m = await messages?.prevPage();
      setMessages((prevMessages) => {
        if (prevMessages) {
          return {
            ...prevMessages,
            hasPrevPage: m.hasPrevPage,
            hasNextPage: m.hasNextPage,
            nextPage: m.nextPage,
            prevPage: m.prevPage,
            items: [...m.items, ...prevMessages.items],
          };
        } else return m;
      });
      setIsFetchingPrevPage(false);
    }
  };

  const handleMessageAdded = (message: Message) => {
    setMessages((prevMessages) => {
      if (prevMessages) {
        return {
          ...prevMessages,
          items: [...prevMessages.items, message],
        };
      } else return null;
    });
    setUnreadMessagesCount((prevCount) => prevCount + 1);
  };

  // Handle message added
  useEffect(() => {
    if (conversation) {
      conversation.on("messageAdded", handleMessageAdded);

      conversation.getMessages().then((m) => {
        setMessages(m);
      });

      conversation.getUnreadMessagesCount().then((count) => {
        setUnreadMessagesCount(count || 0);
      });

      return () => {
        conversation.off("messageAdded", handleMessageAdded);
      };
    }
  }, [conversation]);

  // Get the conversation
  useEffect(() => {
    if (room && isInitialized) {
      client
        ?.getConversationByUniqueName(room.name)
        .then((conversation) => {
          setConversation(conversation);
        })
        .catch((error) => {
          console.error("Error getting conversation", error);
        });
    }
  }, [room, client, isInitialized]);

  // Initialize the client
  useEffect(() => {
    if (!roomToken) {
      return;
    }

    setIsInitialized(false);
    setConversation(null);

    const newClient = new Client(roomToken);
    clientRef.current = newClient;
    setClient(newClient);

    const onInitialized = () => {
      setIsInitialized(true);
    };

    const onConnectionError = (error: unknown) => {
      console.error("Connection error", error);
    };

    newClient.on("initialized", onInitialized);
    newClient.on("connectionError", onConnectionError);

    return () => {
      newClient.off("initialized", onInitialized);
      newClient.off("connectionError", onConnectionError);

      // Attempt to shutdown the client gracefully. If it fails (e.g. timeout during navigation),
      // we catch the error to prevent the Uncaught Promise Rejection.
      (async () => {
        try {
          await newClient.shutdown();
        } catch {
          // Ignore shutdown errors during unmount/navigation
        }
      })();

      if (clientRef.current === newClient) {
        clientRef.current = null;
        setClient(null);
      }
    };
  }, [roomToken]);

  return (
    <ConversationContext.Provider
      value={{
        client,
        conversation,
        messages,
        handleSendMessage,
        handleSendMediaMessage,
        getPrevPage,
        isFetchingPrevPage,
        identity,
        unreadMessagesCount,
        handleReadMessages,
      }}
    >
      {children}
    </ConversationContext.Provider>
  );
};
